import axios from 'axios';
import config from '../config';

const API_BASE_URL = config.api.baseUrl;

export interface User {
  email: string;
  name: string;
  picture?: string;
  sub: string;
  role?: 'CEO' | 'Manager' | 'Supervisor' | 'Employee';
  permissions?: string[];
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken?: string;
}

class AuthService {
  private tokenKey = 'ceo_dashboard_token';
  private refreshTokenKey = 'ceo_dashboard_refresh_token';
  private userKey = 'ceo_dashboard_user';

  // Login with email/password
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      });
      
      const { user, token, refreshToken } = response.data;
      
      // Store tokens and user info
      this.setToken(token);
      if (refreshToken) {
        this.setRefreshToken(refreshToken);
      }
      this.setUser(user);
      
      return { user, token, refreshToken };
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  // Login with Google OAuth
  async loginWithGoogle(googleToken: string): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google`, {
        token: googleToken
      });
      
      const { user, token, refreshToken } = response.data;
      
      // Store tokens and user info
      this.setToken(token);
      if (refreshToken) {
        this.setRefreshToken(refreshToken);
      }
      this.setUser(user);
      
      return { user, token, refreshToken };
    } catch (error) {
      console.error('Google login failed:', error);
      throw new Error('Google login failed. Please try again.');
    }
  }

  // Logout
  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem('user'); // Remove old user data
    localStorage.removeItem('google_token'); // Remove old Google token
    localStorage.removeItem('dev_bypass'); // Remove dev bypass
  }

  // Get current token
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  // Get current user
  getUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  // Check if user has specific permission
  hasPermission(permission: string): boolean {
    const user = this.getUser();
    return user?.permissions?.includes(permission) || false;
  }

  // Refresh token
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem(this.refreshTokenKey);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken
      });
      
      const { token, newRefreshToken } = response.data;
      
      this.setToken(token);
      if (newRefreshToken) {
        this.setRefreshToken(newRefreshToken);
      }
      
      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      throw new Error('Session expired. Please login again.');
    }
  }

  // Get auth headers for API calls
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Set token
  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  // Set refresh token
  private setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  // Set user
  private setUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // Initialize auth state (for app startup)
  initializeAuth(): User | null {
    const token = this.getToken();
    const user = this.getUser();
    
    if (token && user) {
      // Check if token is expired (you might want to add JWT decode logic here)
      return user;
    }
    
    return null;
  }
}

export const authService = new AuthService();
export default authService;
